import { useEffect, useLayoutEffect, useRef, useState, type MutableRefObject } from "react"
import { Input } from "@/components/ui/input"

const COMMIT_DEBOUNCE_MS = 400

type ColumnFilterTextInputProps = {
  fieldName: string
  value: string
  isOpen: boolean
  focusFieldRef: MutableRefObject<string | null>
  onChange: (value: string) => void
}

/** Keeps typing local so the table does not re-render on every keystroke. */
export function ColumnFilterTextInput({
  fieldName,
  value,
  isOpen,
  focusFieldRef,
  onChange,
}: ColumnFilterTextInputProps) {
  const [draft, setDraft] = useState(value)
  const inputRef = useRef<HTMLInputElement>(null)
  const draftRef = useRef(draft)
  const valueRef = useRef(value)
  const onChangeRef = useRef(onChange)
  const debounceRef = useRef<number | undefined>(undefined)

  draftRef.current = draft
  valueRef.current = value
  onChangeRef.current = onChange

  useEffect(() => {
    draftRef.current = value
    if (focusFieldRef.current !== fieldName) {
      setDraft(value)
    }
  }, [value, fieldName, focusFieldRef])

  useEffect(() => {
    return () => {
      if (debounceRef.current !== undefined) {
        window.clearTimeout(debounceRef.current)
      }
    }
  }, [])

  const commit = (next: string) => {
    if (next !== valueRef.current) {
      onChangeRef.current(next)
    }
  }

  const scheduleCommit = (next: string) => {
    if (debounceRef.current !== undefined) {
      window.clearTimeout(debounceRef.current)
    }
    debounceRef.current = window.setTimeout(() => {
      debounceRef.current = undefined
      commit(next)
    }, COMMIT_DEBOUNCE_MS)
  }

  useEffect(() => {
    if (isOpen) return
    if (debounceRef.current !== undefined) {
      window.clearTimeout(debounceRef.current)
      debounceRef.current = undefined
    }
    commit(draftRef.current)
  }, [isOpen])

  useLayoutEffect(() => {
    if (!isOpen) return
    const el = inputRef.current
    if (!el) return
    focusFieldRef.current = fieldName
    el.focus()
    const len = el.value.length
    el.setSelectionRange(len, len)
  }, [isOpen, fieldName, focusFieldRef])

  return (
    <Input
      ref={inputRef}
      type="text"
      value={draft}
      onFocus={() => {
        focusFieldRef.current = fieldName
      }}
      onMouseDown={() => {
        focusFieldRef.current = fieldName
      }}
      onBlur={() => {
        if (debounceRef.current !== undefined) {
          window.clearTimeout(debounceRef.current)
          debounceRef.current = undefined
        }
        commit(draftRef.current)
      }}
      onChange={(e) => {
        const next = e.target.value
        setDraft(next)
        scheduleCommit(next)
      }}
      placeholder="Search"
      className="h-7 px-2 text-[11px] leading-tight bg-white dark:bg-white font-normal"
    />
  )
}
